import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useStore } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import type { RootState } from '@/app/store/index';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { useHomeDealerRecordState } from '@/features/dealers/hooks/useHomeDealerRecord';
import {
    createQuoteSaveLane,
    type PricingOverride,
    type QuoteSaveLane,
    type SaveLaneState,
} from '../services/quoteSaveLane';
import {
    QuoteConflictError,
    PaymentAmountStaleError,
    RetiredSaveError,
    UnsaveableDraftError,
    classifyAmountRefusal,
    failedPricingInputOf,
} from '../services/quoteSaveErrors';
import { applyPricing, buildQuoteSnapshot, toSavedQuote } from '../services/quoteSnapshot';
import { createQuote, updateQuote } from '../services/quotes';
import { toQuoteError } from '../services/quoteErrorMessage';
import { parseSaveMinIntervalMs } from '../services/saveLaneInterval';
import { quoteSaved, setSaveDirty } from '../store/quoteSlice';
import { priceChangedBlock } from '../utils/pricingErrors';
import { basketPricingQueryOptions } from './useBasketPricing';
import {
    usePriceChangeConfirmation,
    type UsePriceChangeConfirmationResult,
} from './usePriceChangeConfirmation';
import { useQuoteErrorModal } from './useQuoteErrorModal';
import { useQuoteStepOrder } from './useQuoteStepOrder';
import { notifyUnsaveableDraft, useSaveRunToasts } from './useSaveRunToasts';
import type { QuoteError } from '../types/quote.types';

const IDLE_STATE: SaveLaneState = {
    phase: 'idle',
    isBusy: false,
    isUnsaved: false,
    lastError: null,
    lastSavedAt: null,
};

const noopSubscribe = () => () => undefined;

const isReconfirmStep = (step: string): boolean => step === 'summary' || step === 'payment';

const alreadySurfaced = (): void => undefined;

export interface QuoteSaveLaneApi {
    /** Background save, eligible for the trailing window. Failures are surfaced, never thrown. */
    requestSave: (options?: { quiet?: boolean }) => void;
    /** Immediate save; rejects with the run's error after surfacing it. */
    save: (options?: { pricingOverride?: PricingOverride }) => Promise<void>;
    flush: (options?: { quiet?: boolean }) => Promise<void>;
    settled: () => Promise<void>;
    /** Blocks new saves until the returned release runs; used while a completion PATCH is in flight. */
    holdSaves: () => () => void;
    getState: () => SaveLaneState;
    isBusy: boolean;
    isUnsaved: boolean;
    /** Failed run on the live lane, or a persisted unsaved marker, read at call time. */
    isUnsavedNow: () => boolean;
    lastError: unknown;
    lastSavedAt: string | null;
    canSave: boolean;
    /** True while the snapshot needs the home dealer record and that request is still loading. */
    awaitingHomeDealer: boolean;
    quoteError: QuoteError | null;
    isErrorModalOpen: boolean;
    openErrorModal: (error: QuoteError) => void;
    closeErrorModal: () => void;
    retryQuoteSave: () => Promise<void>;
    /** Set once a write is refused as stale or terminal; the lane is retired when it is set. */
    conflict: QuoteConflictError | null;
    reportConflict: (error: QuoteConflictError) => void;
    /** The draft id of the current live lane, or null while none exists. */
    laneDraftId: string | null;
    /** Runs `work` and rejects with RetiredSaveError unless the lane that started it still owns the draft;
     *  `work` calls `ensureOwned` after each await to stop at the first point it no longer does. */
    guard: <T>(work: (ensureOwned: () => void) => Promise<T>) => Promise<T>;
    priceChange: UsePriceChangeConfirmationResult;
}

export const useQuoteSaveLane = (): QuoteSaveLaneApi => {
    const store = useStore<RootState>();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const draftId = useAppSelector((state) => state.quote.draftId);
    const quoteData = useAppSelector((state) => state.quote);
    const { defer } = useQuoteStepOrder();
    const homeDealerRecord = useHomeDealerRecordState({ enabled: defer });
    const homeDealer = homeDealerRecord.dealer;
    const errorModal = useQuoteErrorModal();
    const toasts = useSaveRunToasts();
    const readQuote = useCallback(() => store.getState().quote, [store]);
    const priceChange = usePriceChangeConfirmation(readQuote);

    const collaborators = useRef({ defer, homeDealer, errorModal, toasts, priceChange });
    collaborators.current = { defer, homeDealer, errorModal, toasts, priceChange };

    const [lane, setLane] = useState<QuoteSaveLane | null>(null);
    const [laneDraftId, setLaneDraftId] = useState<string | null>(null);
    const [conflict, setConflict] = useState<QuoteConflictError | null>(null);
    const laneRef = useRef<QuoteSaveLane | null>(null);

    useEffect(() => {
        setConflict(null);
        collaborators.current.errorModal.closeErrorModal();
        collaborators.current.priceChange.close();
        if (draftId === '') {
            setLane(null);
            setLaneDraftId(null);
            return undefined;
        }
        const next = createQuoteSaveLane({
            draftId,
            getDraftId: () => store.getState().quote.draftId,
            getSnapshot: () =>
                buildQuoteSnapshot(store.getState().quote, {
                    defer: collaborators.current.defer,
                    homeDealer: collaborators.current.homeDealer,
                }),
            priceBasket: (snapshot) =>
                snapshot.pricingInput
                    ? queryClient.fetchQuery(basketPricingQueryOptions(snapshot.pricingInput))
                    : Promise.reject(new Error('Cannot price the quote: no product selected.')),
            create: async (snapshot, pricing) => {
                try {
                    return toSavedQuote(await createQuote(applyPricing(snapshot.summary, pricing)));
                } catch (error) {
                    throw classifyAmountRefusal(error, snapshot.summary.paymentSelection, pricing);
                }
            },
            update: async (snapshot, pricing, expectedVersion) => {
                try {
                    return toSavedQuote(
                        await updateQuote(
                            snapshot.quoteId ?? '',
                            applyPricing(snapshot.summary, pricing),
                            expectedVersion
                        )
                    );
                } catch (error) {
                    throw classifyAmountRefusal(error, snapshot.summary.paymentSelection, pricing);
                }
            },
            onSaved: (savedDraftId, saved) =>
                dispatch(
                    quoteSaved({
                        draftId: savedDraftId,
                        quoteId: saved.id,
                        quoteRef: saved.quoteRef,
                        status: saved.status,
                        version: saved.version,
                    })
                ),
            setDirty: (dirty) => dispatch(setSaveDirty(dirty)),
            onRunStart: (run) => {
                collaborators.current.errorModal.clearQuoteError();
                collaborators.current.toasts.onRunStart(run);
            },
            onRunSettled: (run, outcome) => collaborators.current.toasts.onRunSettled(run, outcome),
            minIntervalMs: parseSaveMinIntervalMs(import.meta.env.VITE_QUOTE_SAVE_MIN_INTERVAL_MS),
        });
        laneRef.current = next;
        setLane(next);
        setLaneDraftId(draftId);
        return () => {
            next.retire();
            if (laneRef.current === next) laneRef.current = null;
        };
    }, [draftId, store, dispatch, queryClient]);

    const subscribe = useMemo(() => (lane ? lane.subscribe : noopSubscribe), [lane]);
    const laneState = useSyncExternalStore(subscribe, () => lane?.getState() ?? IDLE_STATE);

    const { openErrorModal } = errorModal;
    const { open: openPriceChange } = priceChange;
    const handleConflict = useCallback((error: QuoteConflictError) => {
        laneRef.current?.retire();
        setConflict((current) => current ?? error);
    }, []);

    const reportSaveFailure = useCallback(
        (error: unknown) => {
            if (error instanceof RetiredSaveError) return;
            if (error instanceof UnsaveableDraftError) {
                notifyUnsaveableDraft();
                return;
            }
            if (error instanceof QuoteConflictError) {
                handleConflict(error);
                return;
            }
            console.error('Failed to save quote:', error);
            if (error instanceof PaymentAmountStaleError && isReconfirmStep(store.getState().quote.currentStep)) {
                return;
            }
            const fresh = priceChangedBlock(error);
            if (fresh) {
                openPriceChange(fresh, failedPricingInputOf(error));
                return;
            }
            openErrorModal(toQuoteError(error));
        },
        [openErrorModal, openPriceChange, handleConflict, store]
    );

    const holds = useRef(new WeakMap<QuoteSaveLane, number>());
    const isHeld = useCallback((owner: QuoteSaveLane) => (holds.current.get(owner) ?? 0) > 0, []);
    const holdSaves = useCallback(() => {
        const owner = laneRef.current;
        if (owner === null) return () => undefined;
        holds.current.set(owner, (holds.current.get(owner) ?? 0) + 1);
        let released = false;
        return () => {
            if (released) return;
            released = true;
            holds.current.set(owner, (holds.current.get(owner) ?? 1) - 1);
        };
    }, []);

    const reportIfCurrent = useCallback(
        (owner: QuoteSaveLane, error: unknown) => {
            if (laneRef.current === owner) reportSaveFailure(error);
        },
        [reportSaveFailure]
    );

    const withLane = useCallback(
        (start: (current: QuoteSaveLane) => Promise<void>) => {
            const owner = laneRef.current;
            if (owner === null || isHeld(owner)) return Promise.reject(new RetiredSaveError());
            return start(owner).catch((error: unknown) => {
                if (laneRef.current !== owner) throw new RetiredSaveError();
                reportSaveFailure(error);
                throw error;
            });
        },
        [reportSaveFailure, isHeld]
    );

    const requestSave = useCallback((options?: { quiet?: boolean }) => {
        const owner = laneRef.current;
        if (owner === null || isHeld(owner)) return;
        owner.request(options).catch((error: unknown) => reportIfCurrent(owner, error));
    }, [reportIfCurrent, isHeld]);
    const save = useCallback(
        (options?: { pricingOverride?: PricingOverride }) =>
            withLane((current) => current.flush(options)),
        [withLane]
    );
    const flush = useCallback(
        (options?: { quiet?: boolean }) => withLane((current) => current.flush(options)),
        [withLane]
    );
    const settled = useCallback(
        () => (laneRef.current ? laneRef.current.settled() : Promise.resolve()),
        []
    );

    const guard = useCallback(async <T>(work: (ensureOwned: () => void) => Promise<T>): Promise<T> => {
        const owner = laneRef.current;
        if (owner === null || !owner.owns()) throw new RetiredSaveError();
        const ensureOwned = () => {
            if (laneRef.current !== owner || !owner.owns()) throw new RetiredSaveError();
        };
        let result: T;
        try {
            result = await work(ensureOwned);
        } catch (error) {
            if (!owner.owns()) throw new RetiredSaveError();
            throw error;
        }
        if (!owner.owns()) throw new RetiredSaveError();
        return result;
    }, []);

    const getState = useCallback(() => laneRef.current?.getState() ?? IDLE_STATE, []);
    const isUnsavedNow = useCallback(
        () => (laneRef.current?.getState().isUnsaved ?? false) || store.getState().quote.saveDirty,
        [store]
    );

    const { hideErrorModal } = errorModal;
    const retryQuoteSave = useCallback(async () => {
        hideErrorModal();
        await flush().catch(alreadySurfaced);
    }, [hideErrorModal, flush]);

    const canSave = useMemo(
        () => buildQuoteSnapshot(quoteData, { defer, homeDealer }) !== null,
        [quoteData, defer, homeDealer]
    );

    return {
        requestSave,
        save,
        flush,
        settled,
        holdSaves,
        getState,
        isBusy: laneState.isBusy,
        isUnsaved: laneState.isUnsaved,
        isUnsavedNow,
        lastError: laneState.lastError,
        lastSavedAt: laneState.lastSavedAt,
        canSave,
        awaitingHomeDealer: homeDealerRecord.isLoading && quoteData.selectedDealer === null,
        quoteError: errorModal.quoteError,
        isErrorModalOpen: errorModal.isErrorModalOpen,
        openErrorModal,
        closeErrorModal: errorModal.closeErrorModal,
        retryQuoteSave,
        conflict,
        reportConflict: handleConflict,
        laneDraftId,
        guard,
        priceChange,
    };
};
